import {
  CHILLER,
  CHARACTERISTIC
} from "src/components/shared/ReportDialog/constants"
import {
  CHART_END_DATE,
  CHART_START_DATE,
  CHART_TYPE,
  EQUIPMENT_CATEGORY,
  FACILITY_CATEGORY,
  PARETO_CATEGORY,
  SELECTED_TYPE_EQUIPMENT
} from "../../constant"
import { isJsonString } from "src/utils/CommonMethods"
import { cloneDeep, isArray, uniqBy } from "lodash"
import { API } from "src/shared/lib/amplify"
import { getPropsByEquipmentType } from "src/components/shared/ReportDialog/graphql"
import { decodeURLSearchParams, formatToURLDate } from "../../Helper/common"
import { END_DATE, START_DATE } from "src/blitz/DateRange/constant"
import { sortByAlphanumericSpecialChars } from "src/blitz/DataGridTableNewDesign/helper"
import { CHART_SUB_ID_DIFF } from "./constants"
import {
  evaluateApplicability,
  getCircuitAndCompressorInstances
} from "../../Helper/applicability"

// Function to check if all required Parameters are selected
export const checkAllParamsRequired = (
  isBuildingPerformance,
  chartFilter,
  buildingId
) => {
  let allParamsSelected =
    chartFilter?.[CHART_TYPE]?.value?.id &&
    chartFilter?.[CHART_END_DATE] &&
    chartFilter?.[CHART_START_DATE] &&
    buildingId
  if (isBuildingPerformance) {
    allParamsSelected =
      allParamsSelected && chartFilter?.[SELECTED_TYPE_EQUIPMENT]?.value
  } else {
    allParamsSelected =
      allParamsSelected && chartFilter?.[SELECTED_TYPE_EQUIPMENT]?.value?.id
  }
  return allParamsSelected
}

// Function to check if chart is a Pareto category
export const checkIsPareto = (isBuildingPerformance, selectedTypeEquipment) => {
  return (
    isBuildingPerformance &&
    selectedTypeEquipment &&
    selectedTypeEquipment !== FACILITY_CATEGORY
  )
}

// Function to get applicable properties for Hpath equipment types
export const hPathEquipmentApplicableProperties = async (selectedEquipment) => {
  const selectedEquipmentObjects = [selectedEquipment]
  const equipmentFilter = []
  let compCondTypeFilter = []
  selectedEquipmentObjects?.forEach(({ compCondType, instance }) => {
    const selectedEquipmentFilter = []

    if (compCondType && isJsonString(compCondType)) {
      compCondTypeFilter = JSON.parse(compCondType?.replace(/'/g, '"'))?.map(
        (item) => ({ compCondType: { matchPhrasePrefix: item } })
      )
    } else if (compCondType && compCondType?.includes("[")) {
      compCondTypeFilter = [
        {
          compCondType: {
            matchPhrasePrefix: compCondType.replace(/[\[\]]/g, "")
          }
        }
      ]
    } else if (compCondType && compCondType !== " ") {
      compCondTypeFilter = [
        { compCondType: { matchPhrasePrefix: compCondType } }
      ]
    }

    if (compCondTypeFilter?.length > 0) {
      selectedEquipmentFilter?.push({ or: compCondTypeFilter })
    }
    if (selectedEquipmentFilter?.length > 0) {
      equipmentFilter.push({ and: selectedEquipmentFilter })
    }
  })

  const properties = await fetchPropertiesByEquipmentId(
    equipmentFilter,
    selectedEquipment?.type
  )
  return properties
}
const fetchPropertiesByEquipmentId = async (
  filterVariable = null,
  type = CHILLER
) => {
  try {
    const { data }: any = await API.graphql({
      query: getPropsByEquipmentType,
      variables: {
        filter:
          filterVariable && filterVariable?.length > 0
            ? {
                equipmentType: { eq: type },
                isRDR: { eq: 1 },
                sourceEquipmentType: { eq: "NA" },
                sourceFamilyType: { eq: "NA" },
                or: [...filterVariable]
              }
            : {
                equipmentType: { eq: type },
                isRDR: { eq: 1 },
                sourceEquipmentType: { eq: "NA" },
                sourceFamilyType: { eq: "NA" }
              }
      }
    })
    const uniqueProperties: any = uniqBy(
      data?.searchPropertyMetadata?.items,
      "tisDisplayName"
    )
    const newProperties = uniqueProperties?.map(
      ({ id, targetKey, propertyType, tisDisplayName }) => {
        let propName = targetKey
        try {
          propName =
            propertyType === CHARACTERISTIC
              ? `${tisDisplayName} ***`
              : tisDisplayName
        } catch (error) {
          console.warn(error)
        }
        return {
          name: propName ? propName : targetKey,
          targetKey,
          id: id + propName,
          value: id + propName,
          propertyType
        }
      }
    )
    return newProperties
  } catch (e) {
    console.error(e)
  }
}

export const handleDateChange = (
  pathString,
  val,
  searchParams,
  syncChart,
  position
) => {
  const startDateString = val?.start?.toString()
  const endDateString = val?.end?.toString()

  const startDates = searchParams.get("startDate")
  const endDates = searchParams.get("endDate")

  let startDatesList: any = decodeURLSearchParams(startDates)
  let endDatesList: any = decodeURLSearchParams(endDates)

  const forStartDate = formatToURLDate(startDateString)
  const forEndDate = formatToURLDate(endDateString)

  if (syncChart) {
    startDatesList =
      startDatesList.length === 0
        ? [forStartDate]
        : startDatesList?.map(() => forStartDate)
    endDatesList =
      endDatesList.length === 0
        ? [forEndDate]
        : endDatesList?.map(() => forEndDate)
  } else {
    startDatesList[position] = forStartDate
    endDatesList[position] = forEndDate
  }

  const encodedStartDates = encodeURI(startDatesList)
  const encodedEndDates = encodeURI(endDatesList)
  searchParams.set(START_DATE, encodedStartDates)
  searchParams.set(END_DATE, encodedEndDates)

  pathString = pathString + "?" + searchParams.toString()

  return pathString
}

// Function to build chart options
export const buildChartOptions = (
  chartAxisConfigList = [],
  selectedTypeEquipment,
  isBuildingPerformance,
  equipmentProps: any = []
) => {
  const isPareto = checkIsPareto(
    isBuildingPerformance,
    selectedTypeEquipment?.value
  )
  const chartOptions = chartAxisConfigList?.filter((obj) => {
    if (isPareto) {
      return (
        obj?.value?.type === PARETO_CATEGORY &&
        obj?.value?.equipmentType === selectedTypeEquipment?.value
      )
    } else if (selectedTypeEquipment?.value === FACILITY_CATEGORY) {
      return (
        obj?.value?.category === selectedTypeEquipment?.value &&
        obj?.value?.type !== PARETO_CATEGORY
      )
    } else {
      return (
        obj?.value?.category === EQUIPMENT_CATEGORY &&
        obj?.value?.equipmentType === selectedTypeEquipment?.value?.type
      )
    }
  })
  let options = []
  const { circuitInstanceNumbers, compressorInstances } =
    getCircuitAndCompressorInstances(equipmentProps)
  chartOptions?.forEach((o) => {
    let instances = []
    let isCircuit = null
    let isCompressor = null
    if ((o?.value?.instance || "")?.toLowerCase() === "circuit") {
      instances = circuitInstanceNumbers
      isCircuit = true
    } else if ((o?.value?.instance || "")?.toLowerCase() === "compressor") {
      instances = compressorInstances
      isCompressor = true
    }
    if (o?.value?.category === EQUIPMENT_CATEGORY) {
      if (isCircuit) {
        if (instances?.length) {
          instances?.forEach((inst) => {
            const newObj: any = cloneDeep(o)
            newObj.title = newObj?.title?.replace(
              "#",
              instances?.length === 1 ? "" : inst
            )
            newObj.value.title = newObj?.value?.title?.replace(
              "#",
              instances?.length === 1 ? "" : inst
            )
            newObj.value.id = `${newObj?.value?.id}${CHART_SUB_ID_DIFF}${inst}`
            newObj.value.charac = {
              circuit: inst
            }
            // Check applicability for the chart
            const isChartApplicable = evaluateApplicability(
              newObj?.value?.applicabilityCheck,
              equipmentProps
            )
            if (isChartApplicable) {
              options.push(newObj)
            }
          })
        }
      } else if (isCompressor) {
        if (instances?.length) {
          instances?.forEach((inst) => {
            const newObj: any = cloneDeep(o)
             newObj.title = newObj?.title?.replace(isCircuit ? "#" : "#$", isCircuit ? inst : (instances?.length === 1 ? "" : inst))
             newObj.value.title = newObj?.value?.title?.replace(isCircuit ? "#" : "#$", isCircuit ? inst : (instances?.length === 1 ? "" : inst))
             newObj.value.id = instances?.length === 1 ? `${newObj?.value?.id}` :  `${newObj?.value?.id}${CHART_SUB_ID_DIFF}${inst}`
            newObj.value.charac = {
              compressor: inst
            }
            const isChartApplicable = evaluateApplicability(
              newObj?.value?.applicabilityCheck,
              equipmentProps
            )
            if (isChartApplicable) {
              options.push(newObj)
            }
          })
        }
      } else {
        const isChartApplicable = evaluateApplicability(
          o?.value?.applicabilityCheck,
          equipmentProps
        )
        if (isChartApplicable) {
          options.push(o)
        }
      }
    } else {
      options.push(o)
    }
  })

  options = sortByAlphanumericSpecialChars("title", "Asc", options)
  return options
}
